import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, 
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Chip, Dialog, DialogTitle, 
  DialogContent, DialogActions, Alert, Snackbar, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Rating, Avatar, InputAdornment, useTheme, useMediaQuery
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  PersonOutline as PersonIcon,
  AddOutlined as AddIcon,
  WorkOutline as WorkIcon,
  LocationOnOutlined as LocationIcon,
  EmailOutlined as EmailIcon,
  CheckCircleOutline as CheckCircleIcon,
  ScheduleOutlined as ScheduleIcon,
  AttachMoneyOutlined as MoneyIcon
} from '@mui/icons-material';
import api from '../api';

const ContractorMarketplace = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [contractors, setContractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [bids, setBids] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Dialogs
  const [projectDialog, setProjectDialog] = useState(false);
  const [bidDialog, setBidDialog] = useState(false);
  const [viewBidsDialog, setViewBidsDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Form states
  const [projectData, setProjectData] = useState({
    title: '',
    description: '',
    category: '',
    budget: '',
    deadline: '',
    location: '',
    priority: 'medium'
  });



  const [bidData, setBidData] = useState({
    projectId: '',
    contractorId: '',
    amount: '',
    timeline: '',
    description: ''
  });

  // Filters
  const [filters, setFilters] = useState({
    category: 'all',
    rating: 'all',
    availability: 'all'
  });

  useEffect(() => {
    fetchMarketplaceData();
  }, []);

  const fetchMarketplaceData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching marketplace data...');
      
      // Fetch real properties from API
      const propertiesResponse = await api.get('/api/properties');
      const propertiesData = propertiesResponse.data;
      console.log('📊 Fetched properties:', propertiesData.length);
      console.log('🏠 Properties data structure:', propertiesData);
      setProperties(propertiesData);
      
      // Fetch real contractors from API
      const contractorsResponse = await api.get('/api/contractors');
      const contractorsData = contractorsResponse.data;
      console.log('📊 Fetched contractors:', contractorsData.length);
      
      // Transform contractor data to match marketplace format
      const transformedContractors = contractorsData.map(contractor => ({
        id: contractor.id,
        name: contractor.name,
        category: contractor.specialties?.[0] || 'General', // Use first specialty as category
        rating: contractor.rating || 4.5, // Default rating if not set
        reviews: contractor.reviews || Math.floor(Math.random() * 50) + 10, // Mock reviews for demo
        experience: `${Math.floor(Math.random() * 15) + 5} years`, // Mock experience
        hourlyRate: contractor.hourlyRate || 75,
        location: contractor.address || 'Local Area',
        availability: contractor.isActive ? 'Available' : 'Busy',
        completedProjects: Math.floor(Math.random() * 100) + 20, // Mock project count
        specialties: contractor.specialties || ['General Maintenance'],
        contact: {
          email: contractor.email || 'contact@contractor.com',
          phone: contractor.phone || '(555) 000-0000'
        },
        verified: true, // Assume verified for demo
        insurance: true, // Assume insured for demo
        bonded: Math.random() > 0.3 // Random bonded status
      }));
      
      setContractors(transformedContractors);
      console.log('✅ Contractors transformed:', transformedContractors.length);

      // Fetch real maintenance requests to use as projects
      const maintenanceResponse = await api.get('/api/maintenance-requests');
      const maintenanceData = maintenanceResponse.data;
      console.log('📊 Fetched maintenance requests:', maintenanceData.length);
      console.log('🔍 Sample maintenance request structure:', maintenanceData[0]);
      
      // Transform maintenance requests to projects format
      const transformedProjects = maintenanceData.map(request => {
        // Create a function to get location using the fetched properties data
        const getLocation = () => {
          // Try different field names for property and unit information
          console.log('🔍 Processing location for request:', request.id, '→', {
            propertyName: request.propertyName,
            unitName: request.unitName,
            property: request.property,
            unit: request.unit,
            propertyId: request.propertyId,
            unitId: request.unitId,
            location: request.location
          });
          
          // ALWAYS prioritize dynamic lookup from properties data for current information
          if (request.propertyId && request.unitId) {
            // Try to find the property and unit names from the properties data
            const property = propertiesData.find(p => p.id === request.propertyId);
            console.log('🔍 Property lookup for ID:', request.propertyId, '→', property);
            if (property) {
              const unit = property.units?.find(u => u.id === request.unitId);
              console.log('🔍 Unit lookup for ID:', request.unitId, '→', unit);
              if (unit) {
                return `${unit.name} - ${property.name}`;
              } else {
                return property.name;
              }
            }
          } else {
            console.log('⚠️ No propertyId/unitId for request:', request.id, '→', {
              propertyId: request.propertyId,
              unitId: request.unitId,
              propertyName: request.propertyName,
              unitName: request.unitName
            });
          }
          
          // Fallback to stored property/unit names only if dynamic lookup fails
          if (request.propertyName && request.unitName && 
              !request.propertyName.includes('Unknown') && !request.unitName.includes('Unknown')) {
            return `${request.unitName} - ${request.propertyName}`;
          } else if (request.property && request.unit) {
            return `${request.unit} - ${request.property}`;
          } else if (request.location) {
            return request.location;
          }
          
          // Fallback: try to use propertyName/unitName even if they contain "Unknown"
          if (request.propertyName && request.unitName) {
            return `${request.unitName} - ${request.propertyName}`;
          } else if (request.propertyName) {
            return request.propertyName;
          } else if (request.unitName) {
            return request.unitName;
          }
          
          return 'Property Location';
        };

        return {
          id: request.id,
          title: request.title,
          description: request.description,
          category: request.category || 'General',
          budget: Math.floor(Math.random() * 2000) + 200, // Estimated project cost (mock data for demo)
          deadline: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Random deadline within 30 days
          location: getLocation(),
        priority: request.priority || 'medium',
        status: request.status === 'pending' ? 'bidding' : 
                request.status === 'in_progress' ? 'in-progress' :
                request.status === 'completed' ? 'completed' : 'bidding',
        bids: Math.floor(Math.random() * 5) + 1, // Mock bid count
        postedDate: request.createdAt ? new Date(request.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        assignedTo: request.contractorName || null,
        completedDate: request.status === 'completed' ? new Date().toISOString().split('T')[0] : null
        };
      });
      
      setProjects(transformedProjects);
      console.log('✅ Projects transformed:', transformedProjects.length);

      // Generate mock bids based on projects and contractors for demo
      const mockBids = [];
      transformedProjects.forEach(project => {
        // Generate 1-3 bids per project
        const bidCount = Math.floor(Math.random() * 3) + 1;
        const availableContractors = transformedContractors.filter(c => c.category === project.category || Math.random() > 0.5);
        
        for (let i = 0; i < bidCount && i < availableContractors.length; i++) {
          const contractor = availableContractors[i];
          const baseAmount = project.budget;
          const bidAmount = Math.floor(baseAmount * (0.8 + Math.random() * 0.4)); // 80-120% of budget
          
          mockBids.push({
            id: `bid-${project.id}-${i}`,
            projectId: project.id,
            contractorId: contractor.id,
            contractorName: contractor.name,
            amount: bidAmount,
            timeline: `${Math.floor(Math.random() * 5) + 1} days`,
            description: `Professional ${project.category.toLowerCase()} service with quality materials`,
            status: Math.random() > 0.7 ? 'accepted' : 'pending',
            submittedDate: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          });
        }
      });
      
      setBids(mockBids);
      console.log('✅ Mock bids generated:', mockBids.length);

    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch marketplace data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    try {
      setLoading(true);
      
      // Validation
      if (!projectData.title || !projectData.description || !projectData.budget) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        return;
      }

      const newProject = {
        id: `project-${Date.now()}`,
        ...projectData,
        budget: parseFloat(projectData.budget),
        status: 'bidding',
        bids: 0,
        postedDate: new Date().toISOString().split('T')[0]
      };
      
      setProjects([newProject, ...projects]);
      setProjectDialog(false);
      setSnackbar({ open: true, message: 'Project posted successfully!', severity: 'success' });
      
      // Reset form
      setProjectData({
        title: '',
        description: '',
        category: '',
        budget: '',
        deadline: '',
        location: '',
        priority: 'medium'
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to create project', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const submitBid = async () => {
    try {
      setLoading(true);
      
      const newBid = {
        id: `bid-${Date.now()}`,
        ...bidData,
        amount: parseFloat(bidData.amount),
        status: 'pending',
        submittedDate: new Date().toISOString().split('T')[0]
      };
      
      setBids([newBid, ...bids]);
      setBidDialog(false);
      setSnackbar({ open: true, message: 'Bid submitted successfully!', severity: 'success' });
      
      // Reset form
      setBidData({
        projectId: '',
        contractorId: '',
        amount: '',
        timeline: '',
        description: ''
      });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to submit bid', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const acceptBid = (bidId) => {
    try {
      const bid = bids.find(b => b.id === bidId);
      
      // Update bid status
      setBids(bids.map(b => 
        b.id === bidId ? { ...b, status: 'accepted' } : b
      ));
      
      // Update project status
      setProjects(projects.map(p => 
        p.id === bid.projectId ? { ...p, status: 'in-progress', assignedTo: bid.contractorName } : p
      ));
      
      setSnackbar({ open: true, message: 'Bid accepted! Project assigned to contractor.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to accept bid', severity: 'error' });
    }
  };

  const handleViewBids = (project) => {
    setSelectedProject(project);
    setViewBidsDialog(true);
  };

  const handleSubmitBid = (project) => {
    setBidData({ ...bidData, projectId: project.id });
    setBidDialog(true);
  };

  const getProjectBids = (projectId) => {
    return bids.filter(bid => bid.projectId === projectId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'bidding': return 'warning';
      case 'in-progress': return 'info';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Get unique categories from projects (normalized to lowercase for consistency)
  const availableCategories = [...new Set(projects.map(project => project.category?.toLowerCase()).filter(Boolean))];
  console.log('📊 Available categories:', availableCategories);

  const filteredContractors = contractors.filter(contractor => {
    if (filters.category !== 'all' && contractor.category?.toLowerCase() !== filters.category) return false;
    if (filters.rating !== 'all') {
      const rating = parseFloat(filters.rating);
      if (contractor.rating < rating) return false;
    }
    if (filters.availability !== 'all' && contractor.availability !== filters.availability) return false;
    return true;
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          Contractor Marketplace
        </Typography>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setProjectDialog(true)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Post' : 'Post Project'}
          </Button>
        </Box>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: { xs: 2, sm: 3 } }}
        variant={isMobile ? 'fullWidth' : 'standard'}
      >
        <Tab label="Projects" />
        <Tab label="Contractors" />
        <Tab label="Bids" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Active Projects
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {availableCategories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
            {projects
              .filter(project => filters.category === 'all' || project.category?.toLowerCase() === filters.category)
              .map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.id}>
                <Card>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      mb: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {project.title}
                      </Typography>
                      <Chip 
                        label={project.status} 
                        color={getStatusColor(project.status)}
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </Box>
                    
                    <Typography 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: { xs: 1, sm: 1.5 } }}
                    >
                      {project.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 1 } }}>
                      <Chip 
                        label={project.category} 
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'center' }, 
                      mb: { xs: 1.5, sm: 2 },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                      >
                        <MoneyIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                        Budget: {project.budget}
                      </Typography>
                      <Chip 
                        label={project.priority} 
                        color={getPriorityColor(project.priority)}
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
                    >
                      <LocationIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                      {project.location}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: { xs: 1, sm: 1.5 } }}
                    >
                      <ScheduleIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                      Due: {project.deadline}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'stretch', sm: 'center' }, 
                      mt: { xs: 1.5, sm: 2 },
                      gap: { xs: 1, sm: 1 }
                    }}>
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {project.bids} bids received
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: { xs: 1, sm: 1 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        width: { xs: '100%', sm: 'auto' }
                      }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewBids(project)}
                          fullWidth={isMobile}
                        >
                          View Bids
                        </Button>
                        {project.status === 'bidding' && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSubmitBid(project)}
                            fullWidth={isMobile}
                          >
                            Submit Bid
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Verified Contractors
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {availableCategories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Rating</InputLabel>
                <Select
                  value={filters.rating}
                  onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
                  label="Rating"
                >
                  <MenuItem value="all">All Ratings</MenuItem>
                  <MenuItem value="4.5">4.5+ Stars</MenuItem>
                  <MenuItem value="4.0">4.0+ Stars</MenuItem>
                  <MenuItem value="3.5">3.5+ Stars</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
            {filteredContractors.map((contractor) => (
              <Grid item xs={12} md={6} lg={4} key={contractor.id}>
                <Card>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <Avatar sx={{ mr: { xs: 1, sm: 2 }, width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 } }}>
                        <PersonIcon />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography 
                          variant="h6"
                          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                        >
                          {contractor.name}
                          {contractor.verified && (
                            <CheckCircleIcon color="primary" sx={{ ml: { xs: 0.5, sm: 1 }, fontSize: { xs: 16, sm: 20 } }} />
                          )}
                        </Typography>
                        <Typography 
                          color="textSecondary"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {contractor.category} • {contractor.experience}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
                      <Rating value={contractor.rating} precision={0.1} readOnly size={isMobile ? 'small' : 'small'} />
                      <Typography 
                        variant="body2" 
                        sx={{ ml: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        ({contractor.reviews} reviews)
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 1 } }}
                    >
                      <MoneyIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                      Rate: {contractor.hourlyRate}/hour
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: { xs: 1, sm: 1.5 } }}
                    >
                      <LocationIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                      {contractor.location}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
                      {contractor.insurance && <Chip label="Insured" size="small" color="success" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />}
                      {contractor.bonded && <Chip label="Bonded" size="small" color="info" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />}
                      <Chip 
                        label={contractor.availability} 
                        size="small" 
                        color={contractor.availability === 'Available' ? 'success' : 'warning'}
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, mb: { xs: 1, sm: 1.5 } }}
                    >
                      {contractor.completedProjects} projects completed
                    </Typography>
                    
                    <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, mb: { xs: 0.5, sm: 0.5 } }}
                      >
                        Specialties:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 0.5 }, mt: { xs: 0.5, sm: 0.5 } }}>
                        {contractor.specialties.map((specialty, index) => (
                          <Chip 
                            key={index} 
                            label={specialty} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          />
                        ))}
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      gap: { xs: 1, sm: 1 }, 
                      mt: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        fullWidth={isMobile}
                      >
                        Contact
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setBidData({ ...bidData, contractorId: contractor.id });
                          setBidDialog(true);
                        }}
                        fullWidth={isMobile}
                      >
                        Hire
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
          >
            Recent Bids
          </Typography>
          <TableContainer 
            component={Paper}
            sx={{ 
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Project
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Contractor
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Amount
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Timeline
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bids.map((bid) => (
                  <TableRow key={bid.id}>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 } }}>
                      {projects.find(p => p.id === bid.projectId)?.title}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 } }}>
                      {bid.contractorName}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 } }}>
                      ${bid.amount}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, py: { xs: 0.5, sm: 1 } }}>
                      {bid.timeline}
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 } }}>
                      <Chip 
                        label={bid.status} 
                        color={bid.status === 'accepted' ? 'success' : 'warning'}
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: { xs: 0.5, sm: 1 } }}>
                      {bid.status === 'pending' && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => acceptBid(bid.id)}
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Accept
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Project Dialog */}
      <Dialog 
        open={projectDialog} 
        onClose={() => setProjectDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Post New Project</DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 1.5 }, pb: { xs: 2, sm: 2 } }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Title"
                value={projectData.title}
                onChange={(e) => setProjectData({ ...projectData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={projectData.description}
                onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="project-category-label">Category</InputLabel>
                <Select
                  labelId="project-category-label"
                  value={projectData.category}
                  onChange={(e) => setProjectData({ ...projectData, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="Plumbing">Plumbing</MenuItem>
                  <MenuItem value="Electrical">Electrical</MenuItem>
                  <MenuItem value="HVAC">HVAC</MenuItem>
                  <MenuItem value="General">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Budget"
                type="number"
                value={projectData.budget}
                onChange={(e) => setProjectData({ ...projectData, budget: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Deadline"
                type="date"
                value={projectData.deadline}
                onChange={(e) => setProjectData({ ...projectData, deadline: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="project-priority-label">Priority</InputLabel>
                <Select
                  labelId="project-priority-label"
                  value={projectData.priority}
                  onChange={(e) => setProjectData({ ...projectData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={projectData.location}
                onChange={(e) => setProjectData({ ...projectData, location: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setProjectDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={createProject} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {loading ? 'Posting...' : 'Post Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bid Dialog */}
      <Dialog 
        open={bidDialog} 
        onClose={() => setBidDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Submit Bid</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bid Amount"
                type="number"
                value={bidData.amount}
                onChange={(e) => setBidData({ ...bidData, amount: e.target.value })}
                InputProps={{
                  startAdornment: '$'
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Timeline"
                value={bidData.timeline}
                onChange={(e) => setBidData({ ...bidData, timeline: e.target.value })}
                placeholder="e.g., 2 days"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bid Description"
                multiline
                rows={4}
                value={bidData.description}
                onChange={(e) => setBidData({ ...bidData, description: e.target.value })}
                placeholder="Describe your approach and why you're the best fit for this project..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setBidDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={submitBid} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <WorkIcon />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {loading ? 'Submitting...' : 'Submit Bid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Bids Dialog */}
      <Dialog 
        open={viewBidsDialog} 
        onClose={() => setViewBidsDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Bids for "{selectedProject?.title}"
            </Typography>
            <Typography 
              variant="body2" 
              color="textSecondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {selectedProject && getProjectBids(selectedProject.id).length} bids received
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {selectedProject && (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {selectedProject.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Typography variant="body2">
                  <MoneyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Budget: {selectedProject.budget}
                </Typography>
                <Typography variant="body2">
                  <ScheduleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Deadline: {selectedProject.deadline}
                </Typography>
              </Box>
              
              {getProjectBids(selectedProject.id).length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <WorkIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary">
                    No bids yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    This project hasn't received any bids yet.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Contractor</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Timeline</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Submitted</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getProjectBids(selectedProject.id).map((bid) => (
                        <TableRow key={bid.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                                <PersonIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {bid.contractorName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {contractors.find(c => c.id === bid.contractorId)?.category}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              ${bid.amount}
                            </Typography>
                          </TableCell>
                          <TableCell>{bid.timeline}</TableCell>
                          <TableCell>
                            <Chip 
                              label={bid.status} 
                              color={bid.status === 'accepted' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{bid.submittedDate}</TableCell>
                          <TableCell>
                            {bid.status === 'pending' && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => acceptBid(bid.id)}
                              >
                                Accept
                              </Button>
                            )}
                            {bid.status === 'accepted' && (
                              <Chip label="Selected" color="success" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewBidsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractorMarketplace; 